import React, { useMemo } from "react";
import InputField from "../InputField/InputField";
import CustomInput from "../CustomInput/CustomInput";
import "./PhoneInput.css";
import {
  digitsOnly,
  formatNationalNumber,
  getNationalNumberPlaceholder,
  normalizeCountryCode,
  normalizeNationalNumber,
  toE164,
} from "../../utils/phone";

export default function PhoneInput({
  countryCode,
  phone,
  onChange,
  required = false,
  disabled = false,
  countryCodeDisabled = false,
  phoneDisabled = false,
  variant = "inputfield", // inputfield | custom
  labels = { countryCode: "Ülke Kodu", phone: "Telefon" },
  placeholders,
  countryCodeMaxLength = 4,
  containerStyle,
  countryCodeStyle,
  phoneStyle,
  countryCodeClassName = "",
  phoneClassName = "",
  className = "",
  onPhoneBlur,
  onCountryCodeBlur,
}) {
  const mergedCountryCodeStyle = useMemo(
    () => ({ width: 110, ...(countryCodeStyle || {}) }),
    [countryCodeStyle]
  );
  const mergedPhoneStyle = useMemo(
    () => ({ flex: 1, ...(phoneStyle || {}) }),
    [phoneStyle]
  );

  const cc = useMemo(() => normalizeCountryCode(countryCode, { maxLength: countryCodeMaxLength }), [countryCode, countryCodeMaxLength]);
  const nationalDigits = useMemo(() => normalizeNationalNumber(phone, cc), [phone, cc]);
  const formattedNational = useMemo(() => formatNationalNumber(nationalDigits, cc), [nationalDigits, cc]);

  const ccDisplay = cc ? `+${cc}` : "";
  const phonePlaceholder = placeholders?.phone ?? getNationalNumberPlaceholder(cc);
  const countryCodePlaceholder = placeholders?.countryCode ?? "+90";

  const emitChange = (nextCountryCode, nextPhone) => {
    const normalizedCc = normalizeCountryCode(nextCountryCode, { maxLength: countryCodeMaxLength });
    const normalizedPhone = normalizeNationalNumber(nextPhone, normalizedCc);

    onChange?.({
      countryCode: normalizedCc,
      phone: normalizedPhone,
      countryCodeWithPlus: normalizedCc ? `+${normalizedCc}` : "",
      e164: toE164(normalizedCc, normalizedPhone),
    });
  };

  const handleCountryCodeChange = (e) => {
    const nextCc = normalizeCountryCode(e.target.value, { maxLength: countryCodeMaxLength });
    const nextPhone = normalizeNationalNumber(nationalDigits, nextCc);
    emitChange(nextCc, nextPhone);
  };

  const handlePhoneChange = (e) => {
    const raw = String(e.target.value ?? "");
    const rawDigits = digitsOnly(raw);

    const trimmed = raw.trim();
    if (trimmed.startsWith("+") && cc && rawDigits.startsWith(cc)) {
      emitChange(cc, rawDigits.slice(cc.length));
      return;
    }

    emitChange(cc, rawDigits);
  };

  const isDisabled = disabled || phoneDisabled;
  const isCountryCodeDisabled = disabled || countryCodeDisabled;

  if (variant === "custom") {
    return (
      <div className={`phoneinput phoneinput--custom ${className}`} style={containerStyle}>
        <div style={mergedCountryCodeStyle}>
          <CustomInput
            value={ccDisplay}
            onChange={handleCountryCodeChange}
            placeholder={countryCodePlaceholder}
            disabled={isCountryCodeDisabled}
            maxLength={countryCodeMaxLength + 1}
          />
        </div>
        <div style={mergedPhoneStyle}>
          <CustomInput
            value={formattedNational}
            onChange={handlePhoneChange}
            placeholder={phonePlaceholder}
            disabled={isDisabled}
          />
        </div>
      </div>
    );
  }

  return (
    <div className={`phoneinput phoneinput--inputfield ${className}`} style={containerStyle}>
      <InputField
        type="tel"
        required={required}
        className={`phoneinput-country-code ${countryCodeClassName}`.trim()}
        label={labels?.countryCode}
        placeholder={countryCodePlaceholder}
        value={ccDisplay}
        onChange={handleCountryCodeChange}
        onBlur={onCountryCodeBlur}
        disabled={isCountryCodeDisabled}
        maxLength={countryCodeMaxLength + 1}
        style={mergedCountryCodeStyle}
      />
      <InputField
        type="tel"
        required={required}
        className={phoneClassName}
        label={labels?.phone}
        placeholder={phonePlaceholder}
        value={formattedNational}
        onChange={handlePhoneChange}
        onBlur={onPhoneBlur}
        disabled={isDisabled}
        style={mergedPhoneStyle}
      />
    </div>
  );
}
